import { imageContentType } from '@krp/ui-kit'

/**
 * Кадр сканирования между окнами (RP-FB56). Решение владельца: кадр хранится на УСТРОЙСТВЕ, на сервере — нет. `Blob` в Redux класть
 * нельзя (не сериализуется, десять мегабайт в devtools), поэтому кадр лежит здесь, а в адресе окна и в состоянии — только короткий номер.
 *
 * Хранилище ограничено: держит несколько последних кадров, старые вытесняются (и адрес превью освобождается), — память вкладки не
 * растёт за смену поставки. Обновление страницы стирает всё: окно «Нового товара» тогда откроется с названием и штрихкодом из адреса,
 * но без фото.
 */
export interface ScanFrame {
  blob: Blob
  /** Имя файла для загрузки. */
  name: string
}

interface StoredFrame extends ScanFrame {
  /** Адрес превью, созданный по первому требованию; освобождается вместе с кадром. */
  url: string | null
}

/** Сколько кадров смены держим: текущий, пока открыты окна цепочки, и несколько за ним. */
export const SCAN_FRAME_LIMIT = 5

const frames = new Map<string, StoredFrame>()
let counter = 0

function drop(id: string): void {
  const stored = frames.get(id)
  if (stored === undefined) return
  if (stored.url !== null && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(stored.url)
  frames.delete(id)
}

export function holdScanFrame(blob: Blob, name = 'scan.png'): string {
  counter += 1
  const id = `scan-frame-${counter}`
  frames.set(id, { blob, name, url: null })
  while (frames.size > SCAN_FRAME_LIMIT) {
    const oldest = frames.keys().next()
    if (oldest.done === true) break
    drop(oldest.value)
  }
  return id
}

export function peekScanFrame(id: string): ScanFrame | undefined {
  const stored = frames.get(id)
  return stored === undefined ? undefined : { blob: stored.blob, name: stored.name }
}

/** Адрес превью кадра для `<img>`; `null` — кадра нет (потерян) или браузер не умеет адреса объектов. Создаётся один раз на кадр. */
export function scanFrameUrl(id: string): string | null {
  const stored = frames.get(id)
  if (stored === undefined) return null
  if (stored.url === null && typeof URL.createObjectURL === 'function') stored.url = URL.createObjectURL(stored.blob)
  return stored.url
}

export function releaseScanFrame(id: string): void {
  drop(id)
}

/** Для тестов: чистое хранилище между проверками. */
export function resetScanFramesForTests(): void {
  for (const id of [...frames.keys()]) drop(id)
  counter = 0
}

/** Файл кадра для загрузки: `Blob` из хранилища превращается в `File` с именем, которое ждёт сервер. */
export function scanFrameFile(frame: ScanFrame): File {
  return frame.blob instanceof File ? frame.blob : new File([frame.blob], frame.name, { type: imageContentType({ type: frame.blob.type, name: frame.name }) })
}
